<template>
  <div class="page-wrapper">
    <!-- ===== MOBILE / TABLET VIEW ===== -->
    <div v-if="isMobileOrTablet" class="mobile-layout">
      <appHeader></appHeader>

      <div class="mobile-content">

        <!-- Full-width Banner — no border-radius, no clipping -->
        <div class="mobile-banner-wrap">
          <img
            class="mobile-banner-img"
            v-bind:alt="companyName"
            v-bind:src="companyBannerUrl"
          />
        </div>

        <!-- Welcome Info Section -->
        <section class="hero-section">
          <div class="hero-info container-pad">
            <p class="welcome-label">WELCOME BACK</p>
            <h2 class="welcome-name">{{ userName }}</h2>
            <div v-if="!getIsUserDivyAdmin">
              <router-link :to="{ name: 'AccountHistory' }" class="hdr-points-link">
                <strong>✦ {{ userPointsAvailable | numToLocale }}</strong> pts available
              </router-link>
            </div>
          </div>

          <div class="hero-message container-pad">
            <strong>Welcome to <span v-html="programNameHtml"></span></strong>
            <p v-html="homepageMessageHtml"></p>
          </div>
        </section>

        <!-- What's New -->
        <section v-if="products.length > 0" class="whats-new-section">
          <div class="section-header container-pad">
            <span class="section-tag">New Arrivals</span>
            <h3 class="section-title">What's New</h3>
          </div>
          <appWhatsNewBarHorizontal v-if="!isTabletScreen"></appWhatsNewBarHorizontal>
          <appWhatsNewBar v-else></appWhatsNewBar>
        </section>

        <!-- News -->
        <section v-if="blogItems.length > 0" class="news-section container-pad">
          <appNews></appNews>
        </section>
      </div>

      <!-- Modal: Popup -->
      <b-modal
        id="modal-1"
        v-model="modalShow"
        modal-ok="Got it!"
        ok-only
        scrollable
        visible
        @ok="onCloseModal($event)"
      >
        <p v-html="modalInfo"></p>
        <b-form-checkbox
          id="checkbox-1"
          v-model="modalDontShowAgain"
          name="checkbox-1"
          unchecked-value="not_accepted"
          value="accepted"
        >
          Don't show again
        </b-form-checkbox>
      </b-modal>

      <appFooter></appFooter>
      <bottomNav></bottomNav>
    </div>

    <!-- ===== DESKTOP VIEW ===== -->
    <div v-else class="desktop-layout">
      <appHeader></appHeader>

      <transition appear name="fade">
        <main class="desktop-main">

          <!-- Hero -->
          <section class="desktop-hero">
            <div class="desktop-hero-inner container">
              <div class="desktop-hero-text">
                <p class="desktop-welcome-label">Welcome back</p>
                <h1 class="desktop-welcome-name">{{ userName }}</h1>
                <div v-if="!getIsUserDivyAdmin" class="pb-1">
                  <router-link :to="{ name: 'AccountHistory' }" class="hdr-points-link">
                    <strong>✦ {{ userPointsAvailable | numToLocale }}</strong> pts available
                  </router-link>
                </div>
                <p class="desktop-program-name">
                  <strong>Welcome to <span v-html="programNameHtml"></span></strong>
                </p>
                <p class="desktop-message mb-3" v-html="homepageMessageHtml"></p>
                <p v-if="companyName == 'Pentair'" class="pentair-note">
                  <b>NOTE:</b> $1 earned as a reward in the myHC360+ app = 1 Rewards Mall point.
                </p>

                <div class="desktop-cta-group">
                  <router-link
                    class="cta-btn cta-btn--primary"
                    v-if="customizeCategoryRedirection"
                    :to="{ path: '/shop/category/20254' }"
                  >
                    <font-awesome-icon icon="shopping-cart" />
                    Start Shopping
                  </router-link>
                  <router-link
                    class="cta-btn cta-btn--primary"
                    v-else
                    :to="{ path: '/shop/category/1' }"
                  >
                    <font-awesome-icon icon="shopping-cart" />
                    Start Shopping
                  </router-link>
                  <router-link
                    class="cta-btn cta-btn--secondary"
                    :to="{ name: 'AccountHistory' }"
                  >
                    <font-awesome-icon icon="user" />
                    My Account
                  </router-link>
                </div>
              </div>

              <div class="desktop-hero-image">
                <div class="image-frame">
                  <img
                    v-bind:alt="companyName"
                    v-bind:src="companyBannerUrl"
                  />
                </div>
              </div>
            </div>
          </section>

          <!-- What's New -->
          <section v-if="products.length > 0" class="desktop-whats-new">
            <div class="container">
              <div class="section-header-desktop">
                <span class="section-tag">New Arrivals</span>
                <h3 class="section-title-desktop">What's New</h3>
              </div>
              <appWhatsNewBar></appWhatsNewBar>
            </div>
          </section>

          <!-- News -->
          <div v-if="blogItems.length > 0" class="container">
            <appNews></appNews>
          </div>

          <!-- Modal: Popup -->
          <b-modal
            size="xl"
            id="modal-1"
            v-model="modalShow"
            modal-ok="Got it!"
            ok-only
            scrollable
            visible
            @ok="onCloseModal($event)"
          >
            <p v-html="modalInfo"></p>
            <b-form-checkbox
              id="checkbox-1"
              v-model="modalDontShowAgain"
              name="checkbox-1"
              unchecked-value="not_accepted"
              value="accepted"
            >
              Don't show again
            </b-form-checkbox>
          </b-modal>

          <!-- Modal: NPS Survey -->
          <b-modal
            title="NPS Survey"
            size="lg"
            v-model="npsSurveyModal"
            scrollable
            visible
            hide-footer
            @close="npsSurveyModalClose()"
          >
            <div class="nps-modal">
              <div v-if="npsSurveySubmittedSuccessfully == false" class="nps-form">
                <p class="nps-question">
                  <strong>How likely is it that you would recommend Divvy Engagement to a friend or colleague?</strong>
                </p>

                <div class="nps-scale-wrapper">
                  <span class="nps-scale-label">Not likely</span>
                  <div class="btn-group nps-rating-group" id="ratingDIV" role="group" aria-label="NPS Rating">
                    <button
                      v-for="n in [0,1,2,3,4,5,6,7,8,9,10]"
                      :key="n"
                      type="button"
                      class="btn btn-light nps-btn"
                      :id="String(n)"
                      @click="setNpsSurveryRating(n)"
                    >{{ n }}</button>
                  </div>
                  <span class="nps-scale-label">Very likely</span>
                </div>

                <div class="form-group mt-3">
                  <label for="comment" class="nps-feedback-label">
                    Please provide additional feedback below <span class="text-muted">(optional)</span>.
                  </label>
                  <textarea
                    v-model="notes"
                    class="form-control nps-textarea"
                    rows="4"
                    name="text"
                    placeholder="Share your thoughts..."
                  ></textarea>
                </div>

                <div class="nps-footer">
                  <button type="button" class="cta-btn cta-btn--secondary" @click="npsSurveyModalClose()">Cancel</button>
                  <button type="button" class="cta-btn cta-btn--primary" @click="submitNpsSurvey()" :disabled="isDisabled">
                    Submit
                    <i class="fas fa-spinner fa-spin" v-if="isDisabled"></i>
                  </button>
                </div>
              </div>

              <div v-else class="nps-success">
                <div class="nps-success-icon">✓</div>
                <h4>Thank you!</h4>
                <p>Your feedback means a lot to us.</p>
              </div>
            </div>
          </b-modal>

        </main>
      </transition>

      <appFooter></appFooter>
    </div>
  </div>
</template>

<script>
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import WhatsNewBar from "../../components/WhatsNewBar.vue";
import WhatsNewBarHorizontal from "../../components/WhatsNewBarHorizontal.vue";
import News from "./News.vue";
import { mapState } from "vuex";
import navigationFooterVue from "../../components/navigationFooter.vue";
import { isMobile, isTablet } from "mobile-device-detect";
import FrontendService from "../../services/FrontendService"

// TODO move these to "store"
const LOCALSTORAGE_HIDE_POPUP_LIST = "landingHidePopupList";

export default {
  data() {
    return {
      modalTitle: "", //TODO update DB to support Title+Info fields for Popups

      modalDontShowAgain: false,
      hidePopupList: [],
      npsSurveyModal: false,
      npsSurveryRating: 0,
      notes:"",
      isDisabled: false,
      npsSurveySubmittedSuccessfully: false,
      customizeCategoryRedirection: false,
    };
  },
  computed: {
    ...mapState({
      programNameHtml: (state) => state.company.programNameHtml,
      homepageMessageHtml: (state) => state.company.homepageMessageHtml,
      companyName: (state) => state.company.name,
      companyBannerUrl: (state) => state.company.bannerUrl,
      client: (state) => state.user.profile.clientId,
      userPoints: (state) => state.user.profile.pointsAvailable,
      unapprovedProductPoints: (state) => state.cart.unapproved.productPoints,
      products: (state) => state.product.hotItems,
      blogUrl: (state) => state.company.blogUrl,
      blogItems: (state) => state.feed.items,
      userName: (state) => state.user.profile.firstName,

      notSeenPopups: function(state) {
        return state.company.popups.filter(
          (popup) => !this.hidePopupList.includes(popup.id)
        );
      },
    }),
    userPointsAvailable: function() {
      return this.userPoints - this.unapprovedProductPoints;
    },
    isMobileOrTablet: function() {
      return isMobile || isTablet;
    },
    isTabletScreen: function() {
      return isTablet;
    },
    modalShow: function() {
      return this.notSeenPopups?.length > 0;
    },
    modalInfo: function() {
      return this.notSeenPopups?.[0]?.messageHtml;
    },
    modalId: function() {
      return this.notSeenPopups?.[0]?.id;
    },
    getIsUserDivyAdmin() {
      return this.$store.getters["user/getIsUserDivyAdmin"];
    },
  },
  components: {
    appHeader: Header,
    appFooter: Footer,
    appNews: News,
    appWhatsNewBar: WhatsNewBar,
    appWhatsNewBarHorizontal: WhatsNewBarHorizontal,
    bottomNav: navigationFooterVue,
  },
  methods: {
    onCloseModal: function() {
      if (this.modalDontShowAgain) {
        this.hidePopupList.push(this.modalId);
        localStorage.setItem(
          LOCALSTORAGE_HIDE_POPUP_LIST,
          JSON.stringify(this.hidePopupList)
        );
      }
    },
    setNpsSurveryRating(rating) {
      this.npsSurveryRating = rating;
      $("#ratingDIV .btn-dark").removeClass();
      $("#ratingDIV button").addClass("btn btn-light");
      $("#"+rating).addClass("btn btn-dark");
    },
    submitNpsSurvey() {
      this.isDisabled = true;
      let data = { question: "How likely is it that you would recommend Divvy Engagement to a friend or colleague?", answer: this.npsSurveryRating, notes: this.notes };
      FrontendService.submitNpsSurvey(data)
        .then((response) => {
            this.isDisabled = false;
            this.npsSurveySubmittedSuccessfully = true;
        }).catch((e) => {
          this.isDisabled = false;
          alert(e.response.data.responseMessage);
        });
    },
    showNpsPopup() {
      FrontendService.showNpsPopup()
        .then((response) => {
            this.npsSurveyModal = response.data.data;
        }).catch((e) => {
          alert(e.response.data.responseMessage);
        });
    },
    npsSurveyModalClose() {
      FrontendService.closeNpsPopup()
        .then((response) => {
          this.npsSurveyModal = false
        }).catch((e) => {
          alert(e.response.data.responseMessage);
        });
    },
  },
  beforeMount(){
    this.showNpsPopup();
    this.customizeCategoryRedirection = [500387,500085,500087,252,500247,500030,500268,500199,500200,500053,500340,500355,500333,500289].includes(parseInt(this.client));
  },
  created() {
    this.$store.dispatch("company/fetchCompany");
    this.$store.dispatch("company/fetchPopups");
    this.$store.dispatch("user/fetchProfile");
    if (localStorage.getItem(LOCALSTORAGE_HIDE_POPUP_LIST)) {
      this.hidePopupList = JSON.parse(
        localStorage.getItem(LOCALSTORAGE_HIDE_POPUP_LIST)
      );
    }

    this.$store.dispatch("product/fetchHotProducts", {
      page: this.currentPage,
    });

    this.$store.dispatch("company/fetchCompany").then(() => {
      if (this.blogUrl) {
        this.$store.dispatch("feed/fetchFeed", this.blogUrl);
      }
    });

    console.log(this.blogItems.length > 0);
  },
  watch: {
    blogUrl: function(newValue, oldValue) {
      if (newValue !== null) {
        this.$store.dispatch("feed/fetchFeed", newValue);
      } else {
        this.$store.state.feed.items = [];
      }
    },
  },
};
</script>

<style lang="scss" scoped>

/* ─── Global ────────────────────────────────────────────── */
.page-wrapper {
  min-height: 100vh;
  background-color: #ffffff;
}

.container-pad {
  padding-left: 1.25rem;
  padding-right: 1.25rem;
}

/* Fade transition */
.fade-enter-active { transition: opacity 0.5s ease, transform 0.5s ease; }
.fade-enter       { opacity: 0; transform: translateY(12px); }


/* ─── CTA Buttons (shared) ───────────────────────────────── */
.cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.4rem;
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid transparent;
  transition: all 0.22s ease;
  cursor: pointer;

  &--primary {
    background-color: var(--primary, #007bff);
    color: #fff;
    border-color: var(--primary, #007bff);
    &:hover { filter: brightness(1.1); transform: translateY(-1px); color: #fff; }
  }
  &--secondary {
    background-color: transparent;
    color: var(--primary, #007bff);
    border-color: var(--primary, #007bff);
    &:hover { background-color: var(--primary, #007bff); color: #fff; transform: translateY(-1px); }
  }
}


/* ─── Section labels ─────────────────────────────────────── */
.section-tag {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--primary, #007bff);
  background: rgba(0,123,255,0.08);
  padding: 0.25rem 0.7rem;
  border-radius: 50px;
  margin-bottom: 0.35rem;
}

/* ══════════════════════════════════════════════
   MOBILE LAYOUT
══════════════════════════════════════════════ */
.mobile-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #ffffff;
  padding-bottom: 72px; /* space for bottom nav */
}

.mobile-content {
  flex: 1;
}

/* ── Mobile Banner: full-width, no clip ── */
.mobile-banner-wrap {
  width: 100%;
  overflow: visible;
  margin: 0;
  padding: 0;
  line-height: 0;

  .mobile-banner-img {
    width: 100%;
    height: auto;
    display: block;
    object-fit: contain;
    padding-top: 83px;
  }
}

/* Hero – mobile */
.hero-section {
  background: #fff;
  padding-top: 1.25rem;
  margin-bottom: 0.5rem;
}

.hero-info {
  .welcome-label {
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #888;
    margin: 0 0 0.2rem;
  }
  .welcome-name {
    font-size: 1.75rem;
    font-weight: 900;
    color: #1a1a2e;
    margin: 0 0 0.5rem;
    line-height: 1.1;
  }
  .welcome-divider {
    width: 36px;
    height: 3px;
    background: var(--primary, #007bff);
    border-radius: 2px;
    margin-bottom: 0.85rem;
  }
  .points-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    background: linear-gradient(135deg, var(--primary, #007bff), #0056d2);
    color: #fff;
    border-radius: 50px;
    padding: 0.35rem 0.9rem;
    font-size: 0.8rem;
    font-weight: 600;

    .points-icon { font-size: 0.65rem; }
    .points-value { font-size: 0.95rem; font-weight: 800; }
    .points-label { opacity: 0.85; font-size: 0.75rem; }
  }
}

.hero-message {
  padding-top: 1rem;
  padding-bottom: 0.5rem;
  color: #555;
  font-size: 0.9rem;
  line-height: 1.6;
}

/* What's new – mobile */
.whats-new-section {
  background: #fff;
  padding: 1.25rem 0;
  margin-bottom: 1rem;

  .section-header {
    margin-bottom: 1rem;
    .section-title {
      font-size: 1.15rem;
      font-weight: 800;
      color: #1a1a2e;
      margin: 0;
    }
  }
}


/* News – mobile */
.news-section {
  padding-bottom: 1rem;
}


/* ══════════════════════════════════════════════
   DESKTOP LAYOUT
══════════════════════════════════════════════ */
.desktop-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #ffffff;
}

.desktop-main {
  flex: 1;
  /*padding-top: 5.5rem; /* account for fixed header */
}

/* Desktop Hero */
.desktop-hero {
  background: #fff;
  padding: 3rem 0 2.5rem;
  border-bottom: 1px solid #eee;
}

.desktop-hero-inner {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
}

.desktop-hero-text {
  .desktop-welcome-label {
    font-size: 0.82rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--primary, #007bff);
    margin: 0 0 0.3rem;
  }
  .desktop-welcome-name {
    font-size: 1.4rem;
    font-weight: 900;
    color: #1a1a2e;
    margin: 0 0 0.4rem;
    line-height: 1.1;
  }
  .desktop-program-name {
    font-size: 1rem;
    color: #555;
    margin-bottom: 0.5rem;
  }
  .desktop-message {
    color: #666;
    font-size: 0.93rem;
    line-height: 1.7;
  }
  .pentair-note {
    font-size: 0.85rem;
    color: #555;
    background: #f0f4ff;
    border-left: 3px solid var(--primary, #007bff);
    padding: 0.5rem 0.8rem;
    border-radius: 0 8px 8px 0;
    margin-bottom: 1.25rem;
  }
}

.desktop-cta-group {
  display: flex;
  gap: 0.85rem;
  flex-wrap: wrap;
}

.desktop-hero-image {
  .image-frame {
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 12px 40px rgba(0,0,0,0.12);
    aspect-ratio: 16/9;

    img {
      width: 100%;
      height: 100%;
      display: block;
      transition: transform 0.4s ease;
      &:hover { transform: scale(1.02); }
    }
  }
}

/* Desktop What's New */
.desktop-whats-new {
  background: #ffffff;
  padding: 2.5rem 0;
  margin: 1.5rem 0;

  .section-header-desktop {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 1.25rem;

    .section-title-desktop {
      font-size: 1.4rem;
      font-weight: 800;
      color: #1a1a2e;
      margin: 0;
    }
  }
}


/* ─── NPS Modal Styles ───────────────────────────────────── */
.nps-modal {
  padding: 0.5rem;
}

.nps-question {
  font-size: 1rem;
  color: #1a1a2e;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.nps-scale-wrapper {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;

  .nps-scale-label {
    font-size: 0.75rem;
    color: #888;
    white-space: nowrap;
  }
}

.nps-rating-group {
  flex: 1;
  display: flex !important;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #dee2e6;

  .nps-btn {
    flex: 1;
    padding: 0.5rem 0.2rem;
    font-size: 0.85rem;
    font-weight: 600;
    border: none;
    border-right: 1px solid #dee2e6;
    border-radius: 0 !important;
    &:last-child { border-right: none; }
    &.btn-dark { background-color: var(--primary, #007bff); color: #fff; border-color: var(--primary, #007bff); }
  }
}

.nps-feedback-label {
  font-weight: 600;
  font-size: 0.9rem;
  color: #333;
}

.nps-textarea {
  border-radius: 10px;
  border: 1px solid #ddd;
  resize: none;
  font-size: 0.9rem;
  transition: border-color 0.2s;
  &:focus { border-color: var(--primary, #007bff); box-shadow: 0 0 0 3px rgba(0,123,255,0.12); }
}

.nps-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.25rem;
}

.nps-success {
  text-align: center;
  padding: 2rem 1rem;

  .nps-success-icon {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: #d4edda;
    color: #28a745;
    font-size: 1.8rem;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
  }
  h4 { font-weight: 800; color: #1a1a2e; }
  p  { color: #666; font-size: 0.9rem; }
}


/* ─── Responsive Helpers ─────────────────────────────────── */
@media (max-width: 768px) {
  .desktop-hero-inner {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  .desktop-hero-image { display: none; }
  .desktop-welcome-name { font-size: 1.8rem; }
}
</style>

<style>
/* Unscoped – override Bootstrap modal body */
.modal-body {
  font-weight: unset !important;
}

/* Force white background globally */
body {
  background-color: #ffffff !important;
  background-image: none !important;
}

</style>